import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ArrowLeft, Menu } from 'lucide-react';

const SECTIONS = [
  { id: 'intro', title: 'Introduction' },
  { id: 'getting-started', title: 'Getting Started' },
  { id: 'navigation', title: 'Navigation' },
  { id: 'landing', title: 'Landing Page' },
  { id: 'creating-design', title: 'Creating a Design' },
  { id: 'credits', title: 'Credits System' },
  { id: 'gallery', title: 'Gallery' },
  { id: 'products', title: 'Products Catalog' },
  { id: 'configure-mockup', title: 'Configuring a Mockup' },
  { id: 'mockups', title: 'Your Mockups' },
  { id: 'review-payment', title: 'Review & Payment' },
  { id: 'order-history', title: 'Order History' },
  { id: 'profile', title: 'Profile & Account' },
  { id: 'ready-made', title: 'Ready-Made Designs' },
  { id: 'legal', title: 'Legal Pages' },
  { id: 'admin', title: 'Admin Features' },
  { id: 'order-mgmt', title: 'Order Management (Facility)' },
  { id: 'tours', title: 'Guided Tours' },
  { id: 'flows', title: 'End-to-End Flow Summary' },
];

export default function HelpManualPage() {
  const [tocOpen, setTocOpen] = useState(false);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setTocOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Back + title */}
        <div className="flex items-center gap-4 mb-8">
          <Link
            to="/help"
            className="inline-flex items-center gap-2 text-primary font-semibold hover:underline"
          >
            <ArrowLeft size={20} />
            Back to Help Center
          </Link>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <BookOpen size={32} className="text-primary" />
            User Manual
          </h1>
          <button
            onClick={() => setTocOpen((o) => !o)}
            className="sm:hidden inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-gray-200 font-medium text-gray-700"
          >
            <Menu size={20} />
            Contents
          </button>
        </div>

        <div className="flex gap-8">
          {/* TOC - sticky sidebar on desktop, collapsible on mobile */}
          <nav
            className={`flex-shrink-0 w-56 ${
              tocOpen ? 'block' : 'hidden sm:block'
            }`}
          >
            <div className="sticky top-4 flex flex-col">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Contents
              </p>
              <div className="flex flex-col gap-0.5">
                {SECTIONS.map(({ id, title }) => (
                  <button
                    key={id}
                    onClick={() => scrollTo(id)}
                    className="block w-full text-left text-sm text-gray-600 hover:text-primary hover:underline py-2 border-b border-gray-100 last:border-0"
                  >
                    {title}
                  </button>
                ))}
              </div>
            </div>
          </nav>

          {/* Content */}
          <main className="flex-1 min-w-0 space-y-10">
            <Section id="intro" title="1. Introduction">
              <p className="text-gray-700 mb-4">
                <strong>AI T-Shirt</strong> is an e-commerce platform where you can:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
                <li><strong>Generate</strong> custom designs using AI (text prompts) or upload your own images</li>
                <li><strong>Place</strong> your design on real products (T-shirts, hoodies, etc.) and see a mockup</li>
                <li><strong>Order</strong> print-on-demand items and pay with a card (Stripe)</li>
                <li><strong>Track</strong> orders and manage your designs in one place</li>
              </ul>
              <p className="text-gray-600 text-sm">
                The app uses SuperTokens for sign-in, Stripe for payments, and a print provider (e.g. Printful) for fulfillment.
              </p>
            </Section>

            <Section id="getting-started" title="2. Getting Started">
              <h3 className="font-semibold text-gray-900 mt-4 mb-2">Creating an account</h3>
              <p className="text-gray-700 mb-2">
                Open the app, click &quot;Create Your Own T-Shirt&quot; or use <strong>Generate</strong> in the navbar.
                If you try to generate or use a design without being signed in, a &quot;Create Your Account&quot; modal appears.
                Click <strong>Sign Up Free</strong> or <strong>Sign In</strong>, complete sign-up with email and password (and any email verification if enabled).
              </p>
              <h3 className="font-semibold text-gray-900 mt-4 mb-2">Signing in / out</h3>
              <p className="text-gray-700">
                Use the <strong>Profile</strong> dropdown (top right). Go to <Link to="/auth" className="text-primary font-semibold hover:underline">/auth</Link> to sign in.
                Click <strong>Logout</strong> in the dropdown to sign out.
              </p>
            </Section>

            <Section id="navigation" title="3. Navigation">
              <p className="text-gray-700 mb-2">
                The <strong>navbar</strong> at the top: <strong>AI T-Shirt</strong> (home), <strong>Generate</strong>, <strong>Products</strong>, <strong>Gallery</strong>, <strong>Ready Made</strong>.
                Logged-in users also see <strong>Mockups</strong> and <strong>Order History</strong>.
                Admins see <strong>Admin</strong> and <strong>Assets</strong>; admin or facility roles see <strong>Order Management</strong>.
              </p>
              <p className="text-gray-700">
                In the profile area: <strong>Credits</strong> (coin icon + number) — click to buy more. <strong>Profile dropdown</strong>: Profile, Settings, Logout.
              </p>
            </Section>

            <Section id="landing" title="4. Landing Page">
              <p className="text-gray-700">
                <strong>URL:</strong> <code className="bg-gray-100 px-1 rounded">/</code>. Hero: &quot;Wear Your Imagination&quot; and <strong>Create Your Own T-Shirt</strong> → Generate.
                Shop Our Designs: featured designs with <strong>Buy Now</strong> → Products with that design selected.
                Inspiration slider and footer with legal links.
              </p>
            </Section>

            <Section id="creating-design" title="5. Creating a Design">
              <p className="text-gray-700 mb-2">
                <strong>URL:</strong> <Link to="/design" className="text-primary font-semibold hover:underline">/design</Link>.
                This is where you get the image that will go on a product.
              </p>
              <p className="text-gray-700 mb-2">
                <strong>AI generation:</strong> Enter a prompt, choose style (Default, Anime, Cyberpunk, Sketch), click Generate. Uses credits. If you run out, use the Credits widget to buy more.
              </p>
              <p className="text-gray-700 mb-2">
                <strong>Upload:</strong> Click Upload and select an image — no credits.
              </p>
              <p className="text-gray-700">
                When the design is ready, click <strong>Use This Image</strong> → you go to Products with this image selected.
              </p>
            </Section>

            <Section id="credits" title="6. Credits System">
              <p className="text-gray-700">
                Credits are used for <strong>AI image generation</strong> only. Balance is shown in the navbar (coin icon). Click it to open the Credit Purchase modal — choose a package, enter card details (Stripe), and pay.
              </p>
            </Section>

            <Section id="gallery" title="7. Gallery">
              <p className="text-gray-700">
                <strong>URL:</strong> <Link to="/gallery" className="text-primary font-semibold hover:underline">/gallery</Link> (logged-in). Lists all images you generated or uploaded. Click an image → Products (or Configure Mockup if you came from Products with a product selected). You can select multiple images to delete or remove background.
              </p>
            </Section>

            <Section id="products" title="8. Products Catalog">
              <p className="text-gray-700">
                <strong>URL:</strong> <Link to="/products" className="text-primary font-semibold hover:underline">/products</Link>. Browse, search, filter by type. Selecting a product: if you have a selected image → Configure Mockup; otherwise → Gallery with that product &quot;selected,&quot; then click an image to go to Configure Mockup.
              </p>
            </Section>

            <Section id="configure-mockup" title="9. Configuring a Mockup">
              <p className="text-gray-700">
                <strong>URL:</strong> <Link to="/configure-mockup" className="text-primary font-semibold hover:underline">/configure-mockup</Link>. You must arrive with a product and an image. Set product/variant, placement, stitch color (if applicable), quantity, and <strong>shipping address</strong>. Click <strong>Generate Mockup</strong> — when the mockup image appears, you can proceed to Review → Payment.
              </p>
            </Section>

            <Section id="mockups" title="10. Your Mockups">
              <p className="text-gray-700">
                <strong>URL:</strong> <Link to="/mockups" className="text-primary font-semibold hover:underline">/mockups</Link>. Lists all your mockups. Each card has <strong>Proceed to Payment</strong> → Review Mockup. You can select multiple and delete.
              </p>
            </Section>

            <Section id="review-payment" title="11. Review & Payment">
              <p className="text-gray-700 mb-2">
                <strong>Review Mockup</strong> (<code className="bg-gray-100 px-1 rounded">/review-mockup/:id</code>): Shows mockup, order details, price, shipping. Click <strong>Proceed to Payment</strong>.
              </p>
              <p className="text-gray-700 mb-2">
                <strong>Payment</strong> (<code className="bg-gray-100 px-1 rounded">/payment</code>): Enter card (Stripe). On success → Payment Success page.
              </p>
              <p className="text-gray-700">
                From Payment Success you can go Home or Order History.
              </p>
            </Section>

            <Section id="order-history" title="12. Order History">
              <p className="text-gray-700">
                <strong>URL:</strong> <Link to="/orders" className="text-primary font-semibold hover:underline">/orders</Link>. Table of orders (ID, date, total, payment status, fulfillment status). <strong>View Details</strong> opens the order details page.
              </p>
            </Section>

            <Section id="profile" title="13. Profile & Account">
              <p className="text-gray-700">
                <strong>URL:</strong> <Link to="/profile" className="text-primary font-semibold hover:underline">/profile</Link>. View and edit full name (and email if applicable). Profile is available from the navbar Profile dropdown; Settings and Logout are there too.
              </p>
            </Section>

            <Section id="ready-made" title="14. Ready-Made Designs">
              <p className="text-gray-700">
                <strong>URL:</strong> <Link to="/designs" className="text-primary font-semibold hover:underline">/designs</Link>. Curated designs. <strong>Buy Now</strong> or <strong>Customize</strong> → Products with that design as selected image → same mockup → payment flow.
              </p>
            </Section>

            <Section id="legal" title="15. Legal Pages">
              <p className="text-gray-700">
                <Link to="/terms" className="text-primary font-semibold hover:underline">Terms</Link>, <Link to="/privacy" className="text-primary font-semibold hover:underline">Privacy</Link>, <Link to="/refund-policy" className="text-primary font-semibold hover:underline">Refund policy</Link> — linked from footer and Help Center.
              </p>
            </Section>

            <Section id="admin" title="16. Admin Features">
              <p className="text-gray-700 mb-2">
                For users with <strong>admin</strong> role. <strong>Admin</strong> (<code className="bg-gray-100 px-1 rounded">/admin</code>): Manage Roles, Scheduler Status. <strong>Manage Roles</strong> (<code className="bg-gray-100 px-1 rounded">/admin/roles</code>): Create roles, assign roles to users. <strong>Scheduler Status</strong>: View background jobs. <strong>Assets</strong> (<code className="bg-gray-100 px-1 rounded">/admin/designs</code>): Manage official/curated designs.
              </p>
            </Section>

            <Section id="order-mgmt" title="17. Order Management (Facility)">
              <p className="text-gray-700">
                <strong>URL:</strong> <Link to="/ordermanagement" className="text-primary font-semibold hover:underline">/ordermanagement</Link>. For admin or <code className="bg-gray-100 px-1 rounded">facility_printful</code> role. List orders and update fulfillment status (e.g. processing, shipped).
              </p>
            </Section>

            <Section id="tours" title="18. Guided Tours">
              <p className="text-gray-700">
                The app can show guided tours (Joyride) on key pages. From the <Link to="/help" className="text-primary font-semibold hover:underline">Help Center</Link>, use &quot;Take a guided tour&quot; and pick a tour — we take you to the page and start the tour. You can also start a tour from the relevant page if a tour trigger is available.
              </p>
            </Section>

            <Section id="flows" title="19. End-to-End Flow Summary">
              <p className="text-gray-700 mb-4 font-semibold">Flow A: Create design → Product → Mockup → Pay</p>
              <p className="text-gray-700 mb-4">
                Landing → Create Your Own T-Shirt → Design (generate or upload) → Use This Image → Products → select product → Configure Mockup (shipping, Generate Mockup) → Review Mockup → Proceed to Payment → Payment (Stripe) → Payment Success → Order History.
              </p>
              <p className="text-gray-700 mb-4 font-semibold">Flow B: Ready-made design</p>
              <p className="text-gray-700 mb-4">
                Ready Made → Buy Now → Products → Configure Mockup → Review → Payment → Success → Order History.
              </p>
              <p className="text-gray-700 mb-4 font-semibold">Flow C: From Gallery</p>
              <p className="text-gray-700 mb-4">
                Gallery → click image → Products (or Configure Mockup if product already selected) → same as above.
              </p>
              <p className="text-gray-700 font-semibold">Quick reference</p>
              <p className="text-gray-600 text-sm mt-2">
                Create design → Generate. See my images → Gallery. Choose product + image → Products. Set options + shipping + get mockup → Configure Mockup. See mockups, go to payment → Mockups. Pay → Review Mockup → Payment. See orders → Order History. Buy credits → Credits in navbar. Edit profile → Profile dropdown → Profile.
              </p>
            </Section>
          </main>
        </div>

        <div className="mt-12 pt-8 border-t border-gray-200 text-center">
          <Link to="/help" className="inline-flex items-center gap-2 text-primary font-semibold hover:underline">
            <ArrowLeft size={18} />
            Back to Help Center
          </Link>
        </div>
      </div>
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-xl font-bold text-gray-900 mb-3 border-b border-gray-200 pb-2">
        {title}
      </h2>
      <div className="text-gray-700 prose prose-sm max-w-none">
        {children}
      </div>
    </section>
  );
}
